import React, { useState, useCallback, useEffect } from 'react';
import { Button, Typography, Box, Paper, TextField, Alert, IconButton, MenuItem, FormControl, InputLabel, Select, Autocomplete } from '@mui/material';
import { JobApplication } from '../redux/jobSlice';
import { useDispatch } from 'react-redux';
import { addApplication as addApplicationToStore } from '../redux/jobSlice';
import { addApplication as addApplicationAPI } from '../api/applications';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { MAPBOX_ACCESS_TOKEN } from '../mapboxConfig';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface ConfirmImportProps {
  data: JobApplication[];
  onConfirm: () => void;
  onReset: () => void;
}

const ConfirmImport: React.FC<ConfirmImportProps> = ({ data, onConfirm, onReset }) => {
  const [applications, setApplications] = useState<JobApplication[]>(data);
  const [message, setMessage] = useState<string>('');
  const [showMessage, setShowMessage] = useState(false);
  const [error, setError] = useState<string>('');
  const [locationSuggestions, setLocationSuggestions] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const dispatch = useDispatch();

  const fetchSuggestions = async (query: string) => {
    if (query.toLowerCase() === 'remote') {
      return ['Remote'];
    }

    const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`;
    const params = new URLSearchParams({
      access_token: MAPBOX_ACCESS_TOKEN || '',
      types: 'place',
      limit: '5',
    });

    const response = await fetch(`${endpoint}?${params}`);
    const data = await response.json();
    return data.features.map((feature: any) => feature.place_name);
  };

  const debouncedFetchSuggestions = useCallback(
    debounce(async (input: string) => {
      if (input.length > 2) {
        const suggestions = await fetchSuggestions(input);
        setLocationSuggestions(suggestions);
      } else {
        setLocationSuggestions([]);
      }
    }, 300),
    []
  );

  const handleInputChange = (field: keyof JobApplication, value: string | number) => {
    const updatedApplications = [...applications];
    updatedApplications[currentIndex] = { ...updatedApplications[currentIndex], [field]: value };
    setApplications(updatedApplications);
  };

  const handleLocationChange = (event: React.ChangeEvent<{}>, value: string | null) => {
    const location = value === 'Remote' || value === 'N/A' ? value : (value || '');
    const updatedApplications = [...applications];
    updatedApplications[currentIndex] = { ...updatedApplications[currentIndex], location };
    setApplications(updatedApplications);
  };

  const handleAddApplication = async (application: JobApplication, retries = 3) => {
    try {
      // Send the application to the backend
      const response = await addApplicationAPI(application);

      // Dispatch the action to add the application to the Redux store
      dispatch(addApplicationToStore(response));

      // Remove the application from the local state
      const updatedApplications = applications.filter((_, index) => index !== currentIndex);
      setApplications(updatedApplications);

      // Update the current index if necessary
      if (currentIndex >= updatedApplications.length) {
        setCurrentIndex(Math.max(0, updatedApplications.length - 1));
      }

      setMessage('Application added successfully');
      setShowMessage(true);

      // If all applications have been processed, call onReset
      if (updatedApplications.length === 0) {
        onReset();
      }
    } catch (error) {
      console.error('Error adding application:', error);
      if (retries > 0) {
        console.log(`Retrying... (${retries} attempts left)`);
        setTimeout(() => handleAddApplication(application, retries - 1), 1000);
      } else {
        setError('Failed to add application after multiple attempts. Please try again later.');
        setShowMessage(true);
      }
    }
  };

  const handleDeleteApplication = () => {
    const updatedApplications = applications.filter((_, i) => i !== currentIndex);
    setApplications(updatedApplications);

    if (updatedApplications.length === 0) {
      onReset();
    } else if (currentIndex >= updatedApplications.length) {
      setCurrentIndex(Math.max(0, updatedApplications.length - 1));
    }

    setMessage('Application deleted successfully');
    setShowMessage(true);
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => Math.min(prevIndex + 1, applications.length - 1));
  };

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  useEffect(() => {
    const updateLocations = async () => {
      const updatedApplications = await Promise.all(
        applications.map(async (application) => {
          if (application.location && !application.location.includes(',')) {
            const suggestions = await fetchSuggestions(application.location);
            if (suggestions.length > 0) {
              application.location = suggestions[0];
            }
          }
          return application;
        })
      );
      setApplications(updatedApplications);
    };

    updateLocations();
  }, []);

  //make success/error messages appear for only 5 seconds.
  useEffect(() => {
    if (showMessage) {
      const timer = setTimeout(() => {
        setShowMessage(false);
        setMessage('');
        setError('');
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showMessage]);

  const currentApplication = applications[currentIndex];

  if (applications.length === 0) {
    return null;
  }




  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        mb: 4,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {showMessage && message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {showMessage && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Typography variant="h5" gutterBottom sx={{ color: '#3a7bd5', mb: 2 }}>
        Confirm Imported Applications
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ color: '#3a7bd5' }}>
          Application {currentIndex + 1} of {applications.length}
        </Typography>
        <Box>
          <Button onClick={handlePrevious} disabled={currentIndex === 0} startIcon={<ArrowBackIcon />}>
            Previous
          </Button>
          <Button onClick={handleNext} disabled={currentIndex === applications.length - 1} endIcon={<ArrowForwardIcon />}>
            Next
          </Button>
        </Box>
      </Box>
      {currentApplication && (
        <Box
          sx={{
            position: 'relative',
            p: 2,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(58,123,213,0.2), transparent)',
              animation: 'borderAnimation 5s linear infinite',
            },
            '@keyframes borderAnimation': {
              '0%': {
                transform: 'translateX(-100%)',
              },
              '100%': {
                transform: 'translateX(100%)',
              },
            },
          }}
        >
          <TextField
            fullWidth
            margin="normal"
            label="Company"
            value={currentApplication.company}
            onChange={(e) => handleInputChange('company', e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Position"
            value={currentApplication.position}
            onChange={(e) => handleInputChange('position', e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Autocomplete
            freeSolo
            options={['Remote', 'N/A', ...locationSuggestions]}
            value={applications[currentIndex].location}
            onInputChange={(event, newInputValue) => {
              if (newInputValue !== 'Remote' && newInputValue !== 'N/A') {
                debouncedFetchSuggestions(newInputValue);
              }
            }}
            onChange={handleLocationChange}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                margin="normal"
                label="Location"
                name="location"
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Salary"
            value={currentApplication.salary}
            onChange={(e) => handleInputChange('salary', e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="salary-type-label">Salary Type</InputLabel>
            <Select
              labelId="salary-type-label"
              value={currentApplication.salaryType}
              onChange={(e) => handleInputChange('salaryType', e.target.value)}
              label="Salary Type"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="yearly">Yearly</MenuItem>
              <MenuItem value="hourly">Hourly</MenuItem>
            </Select>
          </FormControl>
          {currentApplication.salaryType === 'hourly' && (
            <TextField
              fullWidth
              margin="normal"
              label="Hours per Week"
              type="number"
              value={currentApplication.hoursPerWeek}
              onChange={(e) => handleInputChange('hoursPerWeek', e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          )}
          <FormControl fullWidth margin="normal">
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              value={currentApplication.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              label="Status"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="Applied">Applied</MenuItem>
              <MenuItem value="Interviewing">Interviewing</MenuItem>
              <MenuItem value="Hired">Hired</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <DatePicker
            label="Date Applied"
            value={currentApplication.dateApplied ? dayjs(currentApplication.dateApplied) : null}
            onChange={(date) => handleInputChange('dateApplied', date ? date.format('YYYY-MM-DD') : '')}
            sx={{
              width: '100%',
              mt: 2,
              mb: 1,
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
              '& .MuiInputBase-root': {
                background: 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
              },
              '& .MuiInputBase-input': { color: '#3a7bd5' },
              '& .MuiIconButton-root': { color: '#3a7bd5' },
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Link"
            value={currentApplication.link}
            onChange={(e) => handleInputChange('link', e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleDeleteApplication}
              sx={{
                flex: 1,
                mr: 1,
                background: 'linear-gradient(to right, #ff4b2b, #ff416c)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(to right, #ff416c, #ff416c)',
                },
              }}
            >
              Delete Application
            </Button>
            <Button
              variant="contained"
              sx={{
                flex: 1,
                ml: 1,
                background: 'linear-gradient(to right, #3a7bd5, #00d2ff)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(to right, #3a7bd5, #3a7bd5)',
                },
              }}
              onClick={() => handleAddApplication(currentApplication)}
            >
              Add Application
            </Button>
          </Box>
        </Box>
      )}
      <Button
        variant="contained"
        sx={{
          mt: 2,
          background: 'linear-gradient(to right, #3a7bd5, #00d2ff)',
          color: 'white',
          '&:hover': {
            background: 'linear-gradient(to right, #3a7bd5, #3a7bd5)',
          },
        }}
        onClick={onConfirm}
      >
        Confirm All
      </Button>
    </Paper>
  );
};

export default ConfirmImport;